/** Экраны отказов (FlowEdge). Каждый отвечает на «что мне теперь делать».
 *
 * Подпись Telegram живёт несколько часов, а Mini App открывают из старого
 * сообщения постоянно — 401 здесь штатное состояние, а не авария. Поэтому у
 * каждого отказа свой текст и своё действие, а не общее «ошибка сети».
 */
import type { Failure } from "../lib/api.ts";

type View = { title: string; text: string; tone: string; icon: string[]; action?: string };

const VIEWS: Record<Failure, View> = {
  expired: {
    title: "Сессия устарела",
    text: "Приложение открыто из старого сообщения. Telegram подписывает вход на несколько часов — надо открыть заново из чата.",
    tone: "var(--warn)",
    action: "Открыть заново",
    icon: [
      "M20.5 11.5A8.5 8.5 0 006.2 5.8L3.5 8.4",
      "M3.5 4v4.4h4.4",
      "M3.5 12.5a8.5 8.5 0 0014.3 5.7l2.7-2.6",
      "M20.5 20v-4.4h-4.4",
    ],
  },
  "no-seat": {
    title: "Мест нет",
    text: "Бот рассчитан на десять человек, и все места заняты. Дело не в тебе — просто некуда посадить. Напиши владельцу.",
    tone: "var(--danger)",
    icon: ["M7.5 10.5V7a4.5 4.5 0 019 0v3.5", "M4.5 10.5h15v10h-15z"],
  },
  offline: {
    title: "Нет связи",
    text: "Цифры показывать не буду: устаревшая сумма хуже отсутствующей — по ней принимают решения.",
    tone: "var(--text-muted)",
    action: "Попробовать ещё раз",
    icon: [
      "M2.5 9.2a14 14 0 0119 0",
      "M5.8 12.6a9.3 9.3 0 0112.4 0",
      "M9.1 16a4.6 4.6 0 015.8 0",
      "M4 4l16 16",
    ],
  },
  "draft-lost": {
    title: "Черновик потерян",
    text: "Пока думали над ответом, сервер перезапустился. Ничего не записалось — набери фразу заново.",
    tone: "var(--text-muted)",
    icon: ["M4 7.5A1.5 1.5 0 015.5 6h13A1.5 1.5 0 0120 7.5v11A1.5 1.5 0 0118.5 20h-13A1.5 1.5 0 014 18.5z", "M8 11h8M8 15h5"],
  },
  gone: {
    title: "Записи больше нет",
    text: "Её удалили — возможно, из чата. Вернись к списку, он уже без неё.",
    tone: "var(--text-muted)",
    icon: ["M5 7h14M10 7V5h4v2M8 7l.8 12.1a1.5 1.5 0 001.5 1.4h3.4a1.5 1.5 0 001.5-1.4L16 7"],
  },
  unclear: {
    title: "Не понял значение",
    text: "Сумму лучше цифрами: 300к, 1.2 млн, 45.",
    tone: "var(--warn)",
    icon: ["M12 7v6", "M12 16.6h.01", "M12 3.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17z"],
  },
  taken: {
    title: "Имя уже занято",
    text: "Категория с таким названием того же типа уже есть. Выбери другое.",
    tone: "var(--warn)",
    icon: ["M12 7v6", "M12 16.6h.01", "M12 3.5a8.5 8.5 0 100 17 8.5 8.5 0 000-17z"],
  },
  "too-long": {
    title: "Запись длинновата",
    text: "Скажи короче — на трату обычно хватает одной фразы.",
    tone: "var(--warn)",
    icon: ["M9 3.5h6v11a3 3 0 01-6 0z", "M5.5 12.5a6.5 6.5 0 0013 0", "M12 19v2"],
  },
  broken: {
    title: "Что-то пошло не так",
    text: "Со стороны сервера. Попробуй через пару секунд — записи в безопасности.",
    tone: "var(--text-muted)",
    action: "Ещё раз",
    icon: [
      "M10.3 3.9L2.6 17.2A2 2 0 004.3 20.2h15.4a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z",
      "M12 9v4",
      "M12 16.6h.01",
    ],
  },
};

export function Fallback({ kind, onRetry }: { kind: Failure; onRetry: () => void }) {
  const view = VIEWS[kind];
  const tint = `color-mix(in srgb, ${view.tone} 12%, transparent)`;
  return (
    // 120px сверху — это воздух, а не запас под чужую панель: в полный экран
    // от него оставалось 15px, и знак съезжал к часам.
    <div style={{ padding: "calc(120px + var(--top-chrome)) 26px 40px", textAlign: "center" }}>
      <div
        style={{
          width: 76, height: 76, borderRadius: 24, margin: "0 auto", background: tint,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={view.tone}
             strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          {view.icon.map((d) => <path key={d} d={d} />)}
        </svg>
      </div>
      <h1 style={{ fontSize: "var(--f-t2)", fontWeight: 700, letterSpacing: "-0.3px",
                   marginTop: "var(--s-5)", marginBottom: 0 }}>
        {view.title}
      </h1>
      <p style={{ fontSize: "var(--f-body)", color: "var(--text-muted)",
                  marginTop: "var(--s-3)", lineHeight: 1.6 }}>
        {view.text}
      </p>
      {view.action && (
        <button type="button" onClick={onRetry}
          style={{ marginTop: "var(--s-5)", minHeight: "var(--tap)", padding: "14px 22px",
                   background: "var(--accent)", color: "var(--on-accent)", border: "none",
                   borderRadius: "var(--r-field)", fontSize: "var(--f-body)", fontWeight: 600,
                   cursor: "pointer", font: "inherit" }}>
          {view.action}
        </button>
      )}
    </div>
  );
}
